import { ModuleInfo, TypeInfo, DataInfo, FuncTypeInfo, FunctionInfo } from '../BinaryReader';
import InstructionDecoder from '../InstructionDecoder';
import { buildControlFlowGraph } from './ControlFlowGraph';
import { buildSsa } from './SsaConstruction';
import { computeDominance } from './DominanceTree';
import { structureFunction } from './StructuralAnalysis';
import { optimizeSsa } from './OptimizationPasses';
import { eliminatePhis } from './PhiElimination';
import { lowerSsaToStatements, proposeLocalNames } from './SsaLowering';
import type { LoweringNameProvider } from './SsaLowering';
import { emitLowered, FieldResolver } from './LoweredEmitter';
import { annotateMemoryPatterns } from './MemoryPatterns';
import { removeStackFrame } from './StackFramePass';
import { materializeFrameBuffer } from './FrameBuffer';
import { promoteFrameLocals, FrameLocals } from './DwarfFrameLocals';
import { propagateCopies } from './CopyPropagation';
import { coalesceCopies } from './CopyCoalescing';
import { recoverStructuredControlFlow } from './StructuredRecovery';
import { wasmValueTypeToC } from './CTypes';
import type { NameResolver } from './NameResolver';

/** A decompiled function split into its signature and its full definition text. */
export interface DecompiledFunction {
  signature: string;
  code: string;
}

export function flattenTypes(moduleInfo: ModuleInfo): TypeInfo[] {
  const flat: TypeInfo[] = [];
  for (const typeEntry of moduleInfo.types) {
    if (typeEntry.kind === 'rec') {
      for (const inner of typeEntry.types) { flat.push(inner); }
    } else {
      flat.push(typeEntry);
    }
  }
  return flat;
}

function getDataSegmentOffset(dataSegment: DataInfo): number | null {
  if (dataSegment.passive || dataSegment.offsetExpr.length === 0) {
    return null;
  }
  // Reuse the shared init-expression decoder (correct signed LEB128) rather than
  // hand-decoding the bytes. Active data offsets are an i32/i64 const expression.
  const instructions = dataSegment.offsetInstructions ?? InstructionDecoder.decodeInitExpr(dataSegment.offsetExpr);
  for (const instruction of instructions) {
    const immediate = instruction.immediates;
    if (immediate.type === 'VarInt32') {
      return immediate.value;
    }
    if (immediate.type === 'VarInt64') {
      return Number(immediate.value);
    }
  }
  return null;
}

// Escapes the backslashes and quotes a rodata string may contain so the emitted C string
// literal stays well-formed. extractString already excludes control characters.
function escapeStringLiteral(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function extractString(data: Uint8Array, offset: number): string | null {
  const PRINTABLE_MIN = 0x20;
  const PRINTABLE_MAX = 0x7f;
  let end = offset;
  while (end < data.length && data[end] !== 0 && data[end] >= PRINTABLE_MIN && data[end] < PRINTABLE_MAX) {
    end++;
  }
  if (end - offset < 2) {
    return null;
  }
  return new TextDecoder().decode(data.slice(offset, end));
}

/**
 * Decompiles one function body to pseudo-C by running the full pipeline
 * (CFG → SSA → optimize → eliminate phis → structure → lower → stack-frame
 * removal → memory patterns → emit). The module, function index, resolver, and
 * field resolver are held as fields so the pipeline steps don't thread them
 * through long parameter lists.
 */
export class FunctionDecompiler {
  private readonly func: FunctionInfo;
  private readonly globalFuncIndex: number;
  private readonly funcType: FuncTypeInfo | null;

  constructor(
    private readonly moduleInfo: ModuleInfo,
    private readonly localFuncIndex: number,
    private readonly nameResolver: NameResolver,
    private readonly fieldResolver?: FieldResolver,
    private readonly frameLocals?: FrameLocals,
  ) {
    const importedFuncCount = moduleInfo.imports.filter(imp => imp.kind === 0).length;
    this.globalFuncIndex = importedFuncCount + localFuncIndex;
    this.func = moduleInfo.functions[localFuncIndex];
    const typeEntry = flattenTypes(moduleInfo)[this.func.typeIndex];
    this.funcType = typeEntry && typeEntry.kind === 'func' ? typeEntry : null;
  }

  decompile(): string {
    return this.decompileDetailed().code;
  }

  // The signature and the full definition text, so a caller assembling a whole module can emit
  // the exact same signature as a forward declaration without re-running the naming pipeline.
  decompileDetailed(): DecompiledFunction {
    const funcType = this.funcType;
    if (!funcType) {
      return { signature: '', code: '// Could not determine function type' };
    }
    try {
      return this.render(funcType);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const signature = this.makeSignature(funcType, localIndex => this.baseLocalName(localIndex));
      return { signature, code: `${signature} {\n  // Decompilation failed: ${errorMessage}\n}` };
    }
  }

  private render(funcType: FuncTypeInfo): DecompiledFunction {
    const instructions = this.func.instructions || InstructionDecoder.decodeFunctionBody(this.func.body);
    const cfg = buildControlFlowGraph(instructions);
    const ssaFunc = buildSsa(cfg, this.moduleInfo, this.localFuncIndex);
    optimizeSsa(ssaFunc);

    // Borrow callee parameter names for otherwise-unnamed locals, applied to the
    // signature, the declared-parameter set, and the body alike. Runs before phi
    // elimination so value-shape detection (e.g. boolean flags) sees the original
    // constant writes rather than the copies out-of-SSA inserts.
    const proposals = proposeLocalNames(
      ssaFunc,
      globalIndex => this.nameResolver.functionName(globalIndex).name,
      localIndex => this.baseLocalName(localIndex),
      (calleeGlobalIndex, parameterIndex) => this.calleeParameterName(calleeGlobalIndex, parameterIndex),
    );
    const effectiveLocalName = (localIndex: number): string => proposals.byLocalIndex.get(localIndex) ?? this.baseLocalName(localIndex);
    const variableName = (variableId: number): string | null => proposals.byVariableId.get(variableId) ?? null;

    eliminatePhis(ssaFunc);
    const structured = structureFunction(ssaFunc, computeDominance(ssaFunc));
    const nameProvider = this.buildNameProvider(effectiveLocalName, variableName);
    const rawLowered = lowerSsaToStatements(structured, ssaFunc, nameProvider);
    const stackFrameResult = removeStackFrame(rawLowered);
    const paramNames = this.paramNames(funcType, effectiveLocalName);
    // When the frame base escapes (a slot address reaches a callee, or it is copied around),
    // it can't be deleted with the prologue; represent the frame as a local buffer. Runs before
    // memory-pattern annotation so frame slots are addressed as &frame[k], not struct fields.
    const framed = materializeFrameBuffer(stackFrameResult.node, stackFrameResult.frameVarName, stackFrameResult.frameSize, paramNames);
    // At low optimization the frame survives prologue removal and DWARF names each frame slot
    // (DW_OP_fbreg). Promote those named slots to scalar locals so `memory[frameBase + k]`
    // reads as the source variable. Runs on raw loads/stores, before memory-pattern annotation.
    const promoted = this.frameLocals
      ? promoteFrameLocals(framed.node, this.frameLocals.slotNames)
      : framed.node;
    // Inline the generated scratch temporaries left by low-optimization local-per-subexpression
    // spilling, so a chain like `var9 = var7; var11 = var9 < var10` collapses to `index < count`.
    const propagated = propagateCopies(promoted);
    const lowered = annotateMemoryPatterns(propagated, stackFrameResult.frameVarName);
    // Recover structured loops/ifs from the goto fallback where the CFG is reducible; the
    // genuinely-irreducible remainder keeps its gotos. (No-op for functions with no labels.)
    const recovered = recoverStructuredControlFlow(lowered);
    // Merge the non-interfering `x = y` copies left by leaving SSA form (phi resolution). Runs
    // last, on the final structure, so liveness sees the loops/ifs as they will be emitted.
    const coalesced = coalesceCopies(recovered, paramNames);

    const signature = this.makeSignature(funcType, effectiveLocalName);
    const code = emitLowered(coalesced, signature, paramNames, this.fieldResolver, framed.buffer);
    return { signature, code };
  }

  private baseLocalName(localIndex: number): string {
    return this.nameResolver.localName(this.globalFuncIndex, localIndex).name;
  }

  // The callee's own name for its parameter, used to name a local passed into that
  // argument. Only a real (name-section / DWARF) name is borrowed; a generated `varN`
  // would carry no information across the call. A direct call's argument count matches
  // the callee's signature, so the argument index is always a parameter index.
  private calleeParameterName(calleeGlobalIndex: number, parameterIndex: number): string | null {
    const resolution = this.nameResolver.localName(calleeGlobalIndex, parameterIndex);
    return resolution.source === 'generated' ? null : resolution.name;
  }

  private makeSignature(funcType: FuncTypeInfo, localName: (localIndex: number) => string): string {
    const returnType = funcType.returnTypes.length === 0 ? 'void'
      : funcType.returnTypes.map(returnT => wasmValueTypeToC(returnT)).join(', ');
    const functionName = this.nameResolver.functionName(this.globalFuncIndex).name;
    const params: string[] = [];
    for (let paramIndex = 0; paramIndex < funcType.parameterTypes.length; paramIndex++) {
      const dwarfType = this.nameResolver.parameterType?.(this.globalFuncIndex, paramIndex);
      const paramType = dwarfType || wasmValueTypeToC(funcType.parameterTypes[paramIndex]);
      params.push(`${paramType} ${localName(paramIndex)}`);
    }
    return `${returnType} ${functionName}(${params.join(', ')})`;
  }

  private paramNames(funcType: FuncTypeInfo, effectiveLocalName: (localIndex: number) => string): Set<string> {
    const names = new Set<string>();
    for (let paramIndex = 0; paramIndex < funcType.parameterTypes.length; paramIndex++) {
      names.add(effectiveLocalName(paramIndex));
    }
    return names;
  }

  private buildNameProvider(
    effectiveLocalName: (localIndex: number) => string,
    variableName: (variableId: number) => string | null,
  ): LoweringNameProvider {
    return {
      functionName: (globalIndex: number): string => this.nameResolver.functionName(globalIndex).name,
      localName: (localIndex: number): string => effectiveLocalName(localIndex),
      globalName: (globalIndex: number): string => this.nameResolver.globalName(globalIndex).name,
      resolveAddress: (address: number): string | null => this.resolveDataAddress(address),
      variableName: (variableId: number): string | null => variableName(variableId),
    };
  }

  private resolveDataAddress(address: number): string | null {
    const globalName = this.nameResolver.resolveGlobalAddress?.(address);
    if (globalName) {
      return globalName;
    }
    for (const dataSegment of this.moduleInfo.data) {
      if (dataSegment.passive) { continue; }
      const segmentOffset = getDataSegmentOffset(dataSegment);
      if (segmentOffset === null) { continue; }
      if (address >= segmentOffset && address < segmentOffset + dataSegment.data.length) {
        const localOffset = address - segmentOffset;
        const stringValue = extractString(dataSegment.data, localOffset);
        if (stringValue && stringValue.length >= 2) {
          const shown = stringValue.length > 32 ? stringValue.slice(0, 32) : stringValue;
          const ellipsis = stringValue.length > 32 ? '...' : '';
          return `"${escapeStringLiteral(shown)}${ellipsis}"`;
        }
      }
    }
    return null;
  }
}
